import { DAY_INDEX_MAP } from '@modules/subscribe/components/SubscribeDrawerV2/OutboundFrequencyCard/constants';
import dayjs from 'dayjs';
import {
  CronValueV2,
  DayName,
  DayOfMonth,
  DayOfMonthInput,
} from 'generated/graphql';

export const getLocalCronValueFromUTCCronValue = (cron?: CronValueV2) => {
  if (!cron) {
    return undefined;
  }

  const userTimeZone = dayjs.tz.guess();
  const cronUTCTime = dayjs()
    .utc()
    .hour(cron.hourOfDay ?? 0)
    .minute(cron.minuteOfHour ?? 0);

  const cronLocalTime = cronUTCTime.tz(userTimeZone);

  const convertUTCDayToLocalDayOfWeek = (dayOfWeek: DayName) => {
    const targetDate = dayjs
      .utc()
      .startOf('month') // start of the month
      .set('day', DAY_INDEX_MAP[dayOfWeek ?? DayName.Invalid]) // go to the required date because we know which day of the week it is
      .set('hour', cron?.hourOfDay ?? 0) // set the hour of the day
      .tz(userTimeZone); // set the system timezone;

    return DayName[targetDate.format('ddd') as keyof typeof DayName];
  };

  const isLocalTimeAheadOfUTCTime = () => {
    // 'isAfter' is not working for timezone dayjs object, so directly comparing the date & time values
    // Ref: https://github.com/iamkun/dayjs/issues/1456
    const cronLocalDate = cronLocalTime.date();
    const cronUTCDate = cronUTCTime.date();

    if (cronLocalDate !== cronUTCDate) {
      return cronLocalDate > cronUTCDate;
    }

    const cronLocalTimeNumber =
      cronLocalTime.hour() + cronLocalTime.minute() / 60;
    const cronLocalUTCNumber = cronUTCTime.hour() + cronUTCTime.minute() / 60;
    return cronLocalTimeNumber > cronLocalUTCNumber;
  };

  const convertUTCDayOfMonthToLocalDayOfMonth = (
    dayOfMonth: DayOfMonthInput,
  ): DayOfMonth => {
    // Custom day of the month is not selected, when "dayOfWeek" is present
    if (!dayOfMonth.day && Boolean(cron.dayOfWeek?.length)) {
      return dayOfMonth;
    }

    // If the day of the month is not selected, but "last: true" is present,
    // then we need to select the first or last day of the month depending on the local timezone
    if (!dayOfMonth.day && dayOfMonth.last) {
      return {
        day: isLocalTimeAheadOfUTCTime() ? 1 : 30,
      };
    }

    // will never happen, but added for type safety
    if (!dayOfMonth.day) {
      return dayOfMonth;
    }

    const localDayOfMonth = dayjs()
      .utc()
      .set('month', 0) // January for full month of 31 days
      .set('date', dayOfMonth.day)
      .set('hour', cron?.hourOfDay ?? 0)
      .set('minute', cron?.minuteOfHour ?? 0)
      .tz(userTimeZone)
      .date();

    return { day: localDayOfMonth };
  };

  const localCronValue: CronValueV2 = {
    __typename: 'CronValueV2',
    dayOfMonth: cron.dayOfMonth
      ? convertUTCDayOfMonthToLocalDayOfMonth(cron.dayOfMonth)
      : cron.dayOfMonth,
    hourOfDay: cronLocalTime.hour(),
    minuteOfHour: cronLocalTime.minute(),
    dayOfWeek: cron.dayOfWeek?.map(convertUTCDayToLocalDayOfWeek),
  };

  return localCronValue;
};
